import React from 'react';
import BrandingFooter from '../common/BrandingFooter';
import TeamBadge, { pickTeamLogo } from '../common/TeamBadge';
import FormPills from '../common/FormPills';
import MatchWidgetShell from '../common/MatchWidgetShell';
import { useWidgetLabels } from '../common/WidgetLabelsContext';

const ZONE_BAR = {
  ucl: '#1e5aa8',
  uel: '#9b2743',
  uecl: '#c9a227',
  relegation: '#e40000',
};

function StatBox({ label, value }) {
  return (
    <div className="sw-meta-tile flex-col items-center text-center !p-3">
      <p className="sw-meta-tile__label">{label}</p>
      <p className="text-xl font-black tabular-nums mt-1">{value ?? '—'}</p>
    </div>
  );
}

export default function TeamProfile({
  showBranding = false,
  competitionLabel = '',
  teamProfile = null,
}) {
  const t = useWidgetLabels();
  const team = teamProfile;
  const zoneColor = team?.zone ? ZONE_BAR[team.zone] : null;

  return (
    <div>
      <MatchWidgetShell competitionLabel={competitionLabel} subtitle={t('teamProfile')}>
        {team ? (
          <>
            <div className="sw-match-hero sw-match-hero--finished relative">
              <div className="sw-match-hero__glow" aria-hidden />
              <div className="relative z-[1] px-5 py-6 flex flex-col items-center text-center">
                <TeamBadge name={team.team} logoUrl={pickTeamLogo(team)} size={72} />
                <h2 className="text-2xl font-black mt-4">{team.team}</h2>
                <p className="text-sm mt-1 font-semibold" style={{ color: 'var(--sw-muted)' }}>
                  #{team.position} · {team.points} {t('points')}
                  {team.played != null ? ` · ${team.played} ${t('played')}` : ''}
                </p>
                {team.zoneLabel && zoneColor && (
                  <span
                    className="mt-3 inline-flex items-center gap-2 text-[10px] font-bold uppercase px-3 py-1.5 rounded-full"
                    style={{
                      background: `${zoneColor}22`,
                      color: zoneColor,
                      border: `1px solid ${zoneColor}55`,
                    }}
                  >
                    <span className="w-2 h-2 rounded-sm" style={{ background: zoneColor }} aria-hidden />
                    {team.zoneLabel}
                  </span>
                )}
                {team.form && (
                  <div className="mt-4 flex items-center gap-2">
                    <span className="text-[10px] font-bold uppercase" style={{ color: 'var(--sw-muted)' }}>
                      {t('form')}
                    </span>
                    <FormPills form={team.form} size={22} />
                  </div>
                )}
              </div>
            </div>
            <div className="p-4">
              <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
                <StatBox label={t('won')} value={team.won} />
                <StatBox label={t('drawn')} value={team.drawn} />
                <StatBox label={t('lost')} value={team.lost} />
                <StatBox label={t('goalsFor')} value={team.gf} />
                <StatBox label={t('goalsAgainst')} value={team.ga} />
                <StatBox label={t('goalDifference')} value={team.gd} />
              </div>
            </div>
          </>
        ) : (
          <p className="text-center text-xs py-10" style={{ color: 'var(--sw-muted)' }}>
            {t('noData')}
          </p>
        )}
      </MatchWidgetShell>
      {showBranding && <BrandingFooter />}
    </div>
  );
}
